import { useState, useMemo } from "react";
import { careLevels, CARE_LEVEL_ORDER, SFA_EMR_CARE_LEVEL_ORDER, WES_CARE_LEVEL_ORDER, AIR_CARE_LEVEL_ORDER } from "@/config/constants";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import InfoBox from "@/components/protocol/InfoBox";
import LinkedText from "@/components/protocol/LinkedText";
import { InlineDoseDisplay, FixedDoseDisplay } from "@/components/protocol/WeightDoseCalculator";
import { hasWeightBasedDosing } from "@/contexts/WeightContext";

const CareLevelTabs = ({ careLevelData }) => {
  // Determine which care level order to use based on the data
  const levelIds = careLevelData.map(l => l.level);
  const isWES = levelIds.includes('wes');
  const isSfaEmr = levelIds.every(l => ['sfa', 'emr'].includes(l));
  const isAir = levelIds.includes('acp_f') || levelIds.includes('ccp');
  const levelOrder = isWES ? WES_CARE_LEVEL_ORDER : isSfaEmr ? SFA_EMR_CARE_LEVEL_ORDER : isAir ? AIR_CARE_LEVEL_ORDER : CARE_LEVEL_ORDER;

  // Get ordered levels that exist in this protocol
  const availableLevels = useMemo(() => {
    return levelOrder.filter(l => levelIds.includes(l));
  }, [careLevelData, levelOrder, levelIds]);

  // Build a map of level -> data for quick lookup
  const levelMap = useMemo(() => {
    const m = {};
    careLevelData.forEach(l => { m[l.level] = l; });
    return m;
  }, [careLevelData]);

  // Default to highest available level
  const [activeTab, setActiveTab] = useState(
    availableLevels[availableLevels.length - 1] || availableLevels[0]
  );

  // Compute cumulative interventions for the active tab
  const cumulativeView = useMemo(() => {
    const activeIdx = levelOrder.indexOf(activeTab);
    const sections = [];

    for (const levelId of levelOrder) {
      if (levelOrder.indexOf(levelId) > activeIdx) break;
      const data = levelMap[levelId];
      if (!data || !data.interventions || data.interventions.length === 0) continue;
      sections.push({
        levelId,
        title: data.title,
        interventions: data.interventions,
        info_boxes: data.info_boxes || [],
        isCurrentLevel: levelId === activeTab,
      });
    }

    return sections;
  }, [activeTab, levelMap, levelOrder]);

  if (availableLevels.length === 0) return null;

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="care-level-tabs">
      <TabsList className="w-full flex flex-wrap h-auto gap-1 p-1" data-testid="care-level-tabs-list">
        {availableLevels.map(levelId => {
          const config = careLevels[levelId] || { shortLabel: levelId.toUpperCase() };
          return (
            <TabsTrigger
              key={levelId}
              value={levelId}
              className={`flex-1 min-w-[60px] text-xs sm:text-sm border border-transparent care-tab-${levelId}`}
              data-testid={`care-tab-${levelId}`}
            >
              {config.shortLabel}
            </TabsTrigger>
          );
        })}
      </TabsList>

      {availableLevels.map(levelId => (
        <TabsContent key={levelId} value={levelId} className="mt-3">
          <CumulativeInterventionList sections={cumulativeView} />
        </TabsContent>
      ))}
    </Tabs>
  );
};

const CumulativeInterventionList = ({ sections }) => {
  return (
    <div className="space-y-4" data-testid="cumulative-interventions">
      {sections.map((section) => {
        const config = careLevels[section.levelId] || { shortLabel: section.levelId.toUpperCase() };
        return (
          <div
            key={section.levelId}
            className={`rounded-lg p-3 sm:p-4 transition-all care-section-${section.levelId}`}
            data-testid={`level-section-${section.levelId}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <Badge
                variant="outline"
                className={`care-badge-${section.levelId} text-xs`}
              >
                {config.shortLabel}
              </Badge>
              <span className="text-sm font-medium text-muted-foreground">
                {section.title}
              </span>
              {!section.isCurrentLevel && (
                <span className="text-xs text-muted-foreground/60 italic ml-auto">included</span>
              )}
            </div>

            {section.info_boxes && section.info_boxes.length > 0 && (
              <div className="mb-3">
                {section.info_boxes.map((box, idx) => (
                  <InfoBox key={idx} type={box.type} title={box.title} content={box.content} image={box.image} imageAlt={box.imageAlt} />
                ))}
              </div>
            )}

            <div className="space-y-1">
              {section.interventions.map((intervention, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex items-start gap-2 py-1 flex-wrap">
                    <span className="text-primary mt-1">•</span>
                    <LinkedText text={intervention.text} />
                    {hasWeightBasedDosing(intervention.text) ? (
                      <InlineDoseDisplay text={intervention.text} />
                    ) : (
                      <FixedDoseDisplay text={intervention.text} />
                    )}
                  </div>

                  {intervention.sub_steps && intervention.sub_steps.length > 0 && (
                    <div className="ml-5 pl-3 border-l-2 border-border/50 space-y-0.5">
                      {intervention.sub_steps.map((sub, subIdx) => (
                        <div key={subIdx} className="text-sm py-0.5 flex items-start gap-2 flex-wrap">
                          <span className="mt-0.5">◦</span>
                          <LinkedText text={sub} />
                          {hasWeightBasedDosing(sub) ? (
                            <InlineDoseDisplay text={sub} />
                          ) : (
                            <FixedDoseDisplay text={sub} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CareLevelTabs;
